// 증상 예측 카드 컴포넌트
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SymptomPrediction } from '@/api/mentalHealth/report.types';

interface SymptomPredictionCardProps {
  prediction: SymptomPrediction;
}

export const SymptomPredictionCard: React.FC<SymptomPredictionCardProps> = ({ prediction }) => {
  const { t } = useTranslation();

  const getRiskLabel = (level: string) => {
    switch (level) {
      case 'insufficient_data': return t('mentalHealth.status.noInfo');
      case 'low': return t('mentalHealth.symptom.level.normal');
      case 'moderate': return t('mentalHealth.status.caution');
      case 'high': return t('mentalHealth.status.caution');
      case 'very_high': return t('mentalHealth.status.risk');
      default: return t('mentalHealth.status.noInfo');
    }
  };

  const getSymptomCode = (name: string) => {
    if (name.includes('stress')) return 'ST';
    if (name.includes('breathing')) return 'BR';
    if (name.includes('pain')) return 'PN';
    if (name.includes('depression')) return 'DP';
    if (name.includes('cardiovascular')) return 'CV';
    if (name.includes('bloodPressure')) return 'BP';
    return 'RS';
  };

  const hasIndicators = prediction.indicators.length > 0
    && prediction.indicators[0] !== 'symptom.indicator.insufficientData'
    && prediction.indicators[0] !== 'symptom.indicator.normalRange';
  const stabilityScore = prediction.riskLevel !== 'insufficient_data' && prediction.confidence > 0
    ? 100 - Math.round(prediction.score)
    : null;

  return (
    <article data-pdf-block className={`symptom-risk-card symptom-risk-card--${prediction.riskLevel}`}>
      <header className="symptom-risk-card__header">
        <div className="symptom-risk-card__title">
          <span>{getSymptomCode(prediction.symptomName)}</span>
          <div>
            <small>{t('mentalHealth.patternAnalysis')}</small>
            <h4>{t(prediction.symptomName)}</h4>
          </div>
        </div>
        <div className="symptom-risk-card__level">
          <span>{getRiskLabel(prediction.riskLevel)}</span>
          <small>{t('mentalHealth.dataSufficiency')} {Math.round(prediction.confidence * 100)}%</small>
        </div>
      </header>

      <div className="symptom-risk-card__body">
        <div className="symptom-risk-card__score">
          <div>
            <span>{t('mentalHealth.stabilityIndex')}</span>
            <strong>{stabilityScore ?? '-'}</strong>
            <small>/ 100</small>
          </div>
          <div className="symptom-risk-card__bar">
            <span style={{ width: `${stabilityScore ?? 0}%` }} />
          </div>
        </div>
        <p className="symptom-risk-card__description">{t(prediction.description)}</p>

        {hasIndicators && (
          <div className="symptom-risk-card__signals">
            <strong>{t('mentalHealth.symptomCard.signals')}</strong>
            <div>
            {prediction.indicators.map((indicator, index) => (
                <span key={index}>{t(indicator)}</span>
            ))}
            </div>
          </div>
        )}

        {prediction.metrics && (
          <details className="symptom-risk-card__metrics">
            <summary>{t('mentalHealth.symptomCard.viewKeyMetrics')}</summary>
            <div>
              {Object.entries(prediction.metrics).map(([key, value]: [string, string | number]) => (
                <p key={key}><span>{key}</span><strong>{value}</strong></p>
              ))}
            </div>
          </details>
        )}

        {prediction.confidence === 0 && <p className="symptom-risk-card__unavailable">{t('common.insufficientData')}</p>}
      </div>
    </article>
  );
};
